// Code 128 encoder.
//
// Stock stickers are only 2 inches wide, so the barcode has to be as compact
// as it can be while staying readable by an ordinary USB laser/CCD scanner.
// That rules out a QR code (needs a 2D imager) and rules out plain Code 128-B
// (one symbol per character). This encoder switches into Code 128-C for runs
// of digits, which packs two digits into one symbol and roughly halves the
// width of a serial like CC-2026-000123.
//
// Output is a list of bar/space widths in modules, which the caller scales to
// the space it has and draws as vector rectangles — crisp at any print size,
// with no image conversion and no extra dependency.

// The 107 Code 128 symbol patterns. Each string alternates bar,space widths.
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
  '211214', '211232', '2331112',
];

const START_B = 104;
const START_C = 105;
const CODE_B = 100;
const CODE_C = 99;
const STOP = 106;

const isDigit = (ch) => ch >= '0' && ch <= '9';

// Length of the digit run starting at `from`.
function digitRunLength(text, from) {
  let n = 0;
  while (from + n < text.length && isDigit(text[from + n])) n += 1;
  return n;
}

// Code C is worth entering for a run of 4+ digits mid-string (the switch costs
// one symbol but saves one per digit pair), or 2+ at the very start or end.
function shouldUseC(text, position) {
  const run = digitRunLength(text, position);
  if (run < 2) return false;
  const atStart = position === 0;
  const atEnd = position + run === text.length;
  if ((atStart || atEnd) && run >= 4) return true;
  return run >= 6;
}

// Turn text into the list of Code 128 values, switching between B and C.
export function encodeToValues(input) {
  const text = String(input || '');
  if (!text) throw new Error('Nothing to encode.');
  // Code 128-B covers printable ASCII; anything else cannot be represented.
  for (const ch of text) {
    const code = ch.charCodeAt(0);
    if (code < 32 || code > 126) throw new Error(`Cannot encode character "${ch}".`);
  }

  const values = [];
  let mode = shouldUseC(text, 0) ? 'C' : 'B';
  values.push(mode === 'C' ? START_C : START_B);

  let i = 0;
  while (i < text.length) {
    if (mode === 'C') {
      // Code C only handles digit pairs — drop back to B for anything else or
      // for a lone trailing digit.
      if (isDigit(text[i]) && isDigit(text[i + 1])) {
        values.push(Number(text.substr(i, 2)));
        i += 2;
        continue;
      }
      values.push(CODE_B);
      mode = 'B';
      continue;
    }

    if (shouldUseC(text, i)) {
      values.push(CODE_C);
      mode = 'C';
      continue;
    }
    values.push(text.charCodeAt(i) - 32);
    i += 1;
  }

  // Modulo-103 weighted checksum over the start code and payload.
  let checksum = values[0];
  for (let index = 1; index < values.length; index += 1) {
    checksum += values[index] * index;
  }
  values.push(checksum % 103);
  values.push(STOP);
  return values;
}

// Bar/space widths in modules, starting with a bar and alternating.
// The caller adds the quiet zone.
export function encodeToModules(input) {
  const values = encodeToValues(input);
  const widths = [];
  for (const value of values) {
    for (const digit of PATTERNS[value]) widths.push(Number(digit));
  }
  return widths;
}

// Total module count including the 10-module quiet zone each side that the
// spec requires for a reliable read.
export const QUIET_ZONE_MODULES = 10;

export function totalModules(input) {
  return encodeToModules(input).reduce((sum, w) => sum + w, 0) + QUIET_ZONE_MODULES * 2;
}

// Bars only, as {x, width} pairs in module units measured from the left edge of
// the quiet zone. Spaces are simply the gaps between them.
export function barRects(input) {
  const widths = encodeToModules(input);
  const bars = [];
  let x = QUIET_ZONE_MODULES;
  widths.forEach((width, index) => {
    if (index % 2 === 0) bars.push({ x, width }); // even index = bar
    x += width;
  });
  return bars;
}
